import React, { useMemo, useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassButton } from 'dashboard/components/common';

/**
 * Change your own password.
 *
 * The rules mirror the server's `validatePasswordStrength`, so someone is
 * told what is wrong while typing rather than after a round trip that
 * rejects them with a list.
 */
const RULES = [
  { test: (v) => v.length >= 8, label: 'At least 8 characters' },
  { test: (v) => /[A-Z]/.test(v), label: 'One uppercase letter' },
  { test: (v) => /[a-z]/.test(v), label: 'One lowercase letter' },
  { test: (v) => /[0-9]/.test(v), label: 'One number' },
];

export default function AccountPasswordTab({ onDone }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const failed = useMemo(() => RULES.filter((r) => !r.test(newPassword)), [newPassword]);
  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const ready = currentPassword && newPassword && failed.length === 0 && confirm === newPassword;

  const disabledReason = !currentPassword
    ? 'Enter your current password.'
    : failed.length > 0
      ? `New password still needs: ${failed.map((r) => r.label.toLowerCase()).join(', ')}.`
      : confirm !== newPassword
        ? 'The two new passwords do not match.'
        : '';

  const submit = () => {
    setBusy(true);
    API.changePassword({ currentPassword, newPassword })
      .then(() => {
        toast.success('Password changed');
        setCurrent(''); setNext(''); setConfirm('');
        onDone?.();
      })
      .catch((err) => toast.error(err?.message || 'Could not change your password'))
      .finally(() => setBusy(false));
  };

  return (
    <Stack spacing='16px'>
      <Typography variant='body2' color='text.secondary'>
        Changing this affects how you sign in to TechProOne. It has no effect on any
        brand's customers or their accounts.
      </Typography>

      <TextField
        type='password' size='small' label='Current password' autoComplete='current-password'
        value={currentPassword} onChange={(e) => setCurrent(e.target.value)} fullWidth
      />
      <TextField
        type='password' size='small' label='New password' autoComplete='new-password'
        value={newPassword} onChange={(e) => setNext(e.target.value)} fullWidth
      />
      <TextField
        type='password' size='small' label='Confirm new password' autoComplete='new-password'
        value={confirm} onChange={(e) => setConfirm(e.target.value)} fullWidth
        error={mismatch} helperText={mismatch ? 'These do not match.' : ' '}
      />

      <Stack spacing='4px'>
        {RULES.map((r) => {
          const ok = r.test(newPassword);
          return (
            <Typography
              key={r.label}
              variant='caption'
              sx={{ color: newPassword.length === 0 ? 'text.disabled' : ok ? 'success.main' : 'text.secondary' }}
            >
              {ok ? '✓' : '•'} {r.label}
            </Typography>
          );
        })}
      </Stack>

      <Stack direction='row' justifyContent='flex-end'>
        <GlassButton
          variant='contained' onClick={submit}
          disabled={!ready || busy} disabledReason={disabledReason}
          sx={{ borderRadius: '14px', px: '26px', height: 44 }}
        >
          {busy ? 'Changing…' : 'Change password'}
        </GlassButton>
      </Stack>
    </Stack>
  );
}
